/**
 * 軍札（いくさふだ）— 兵種と、章ごとの山札の構成
 *
 * docs/06-賭けと軍配勝負.md §2
 *
 * **どこまでを data に置くかの線引き**
 *
 * ここに置くのは「どの兵種が、いつ、何枚あるか」と、その史実の背景だけ。
 * 札の符号化（`cardId`）とランクの上限、役の判定は core に残す。
 * 役の順位は全数列挙で確定させた値（`docs/06 §16`）なので、
 * データ側の編集で静かに壊れてよいものではない。
 *
 * 逆に、鉄砲がいつ現れ、僧兵がいつ減るかは**史実の話**であって
 * ルールの話ではない。だから盤面と同じくデータから注入する。
 */

import { RANK_MAX, type Arm, type DeckSpec } from '@tenka/core';
import type { HistoryNote } from './types';

export interface ArmInfo {
  readonly arm: Arm;
  readonly name: string;
  readonly reading: string;
  readonly note: HistoryNote;
}

// ---------------------------------------------------------------------------
// 兵種
// ---------------------------------------------------------------------------

export const ARM_ASHIGARU: ArmInfo = {
  arm: 'ashigaru',
  name: '足軽',
  reading: 'あしがる',
  note: {
    summary: '軽い装備で戦う歩兵。長い槍をそろえて隊列で戦い、戦国の合戦の主力になった。',
    detail: {
      1: '足軽は、軽いよろいで戦う歩兵です。馬に乗る武士とちがい、長い槍を持って何人もならび、集団で戦いました。応仁の乱のころから目立つようになり、戦国時代には合戦の主役になります。',
      2: '足軽は軽装の歩兵で、応仁の乱（1467〜77）のころから戦場で目立つ存在になった。戦国期には長柄槍を持つ足軽が隊列を組んで戦う戦法が広まり、合戦の主力となる。大名は足軽を組織的に編成し、足軽大将が指揮した。長柄槍は長いもので4メートルを超え、個人の技量より隊列の統制がものをいう武器だった。',
      3: '足軽は軽装の歩兵を指す語で、応仁の乱の時期に略奪や放火を担う無頼の徒としても記録され、当初は必ずしも正規の戦力ではなかった。しかし戦国期に入ると、大名は足軽を足軽組として常備的に編成し、長柄槍・弓・鉄砲といった兵種ごとの部隊を足軽大将の下に置くようになる。長柄槍は3〜6メートルに及び、個人の武勇より隊列の統制と一斉動作が戦果を左右した。この変化は、合戦が騎馬武者どうしの一騎討ちから、歩兵集団の運用へ移っていく過程と重なる。豊臣政権の兵農分離政策は、この足軽層を身分として固定していく方向に働いた。',
    },
    keywords: ['足軽', '長柄槍', '応仁の乱', '兵農分離', '歩兵'],
    caveat:
      '「足軽＝動員された農民」という説明は単純化されすぎている。戦国期の足軽には専業化した者、地侍や被官として主家に仕える者など多様な出自が含まれ、一律に農民とはいえない。',
  },
};

export const ARM_YUMI: ArmInfo = {
  arm: 'yumi',
  name: '弓',
  reading: 'ゆみ',
  note: {
    summary: '和弓を用いる遠戦の兵。鉄砲が伝わったあとも、速さと雨に強い点で使われ続けた。',
    detail: {
      1: '弓は、遠くの敵をねらう武器です。日本の弓は長くて、上より下の方が短い形をしています。鉄砲が伝わったあとも、弓は矢を早く続けて放てるので、戦場から消えることはありませんでした。',
      2: '和弓は長さ2メートルを超える長弓で、握りが中央より下にあるのが特徴である。戦国期の合戦では、まず遠戦兵器で射合い、そのうえで槍を合わせるのが基本の順序だった。鉄砲伝来後も弓が廃れなかったのは、連射がきくこと、雨天でも使えること、そして射手の育成に時間はかかるが弾薬の補給に左右されないことによる。',
      3: '和弓は全長221センチ前後の長弓で、握りを中央より下に置く独特の形状をもつ。中世の武士にとって弓馬は本領であり、「弓馬の道」が武芸の代名詞であった。戦国期の合戦は、弓・鉄砲による遠戦から槍衾（やりぶすま）の接触へ移行するのが通例で、遠戦兵器の比重はむしろ増している。鉄砲が普及しても弓が併用され続けた理由は複合的で、発射速度、悪天候下での確実性、火薬と鉛という輸入依存の消耗品を必要としないこと、そして曲射によって遮蔽物の背後を狙えることが挙げられる。近年の軍事史研究では、鉄砲が弓を「置き換えた」という単線的な理解は退けられている。',
    },
    keywords: ['和弓', '長弓', '遠戦', '弓馬の道', '曲射'],
  },
};

export const ARM_KIBA: ArmInfo = {
  arm: 'kiba',
  name: '騎馬',
  reading: 'きば',
  note: {
    summary: '馬に乗って戦う武士。ただし集団で突撃する「騎馬隊」の姿は、後世のものが大きい。',
    detail: {
      1: '馬に乗って戦う武士を騎馬といいます。ただし、戦国時代の日本の馬は今の競走馬よりずっと小さく、馬に乗ったまま突っこむより、戦場まで馬で行って降りて戦うことも多かったと考えられています。',
      2: '騎馬は馬上で戦う武士を指す。中世の武士にとって騎射は本領だったが、戦国期の在来馬は体高130センチ前後と小型で、現代の競走馬とは大きく異なる。近年の研究では、騎馬武者が集団で密集突撃する「騎馬隊」の姿は、江戸時代の軍記物によって強調されたイメージであり、実際には下馬して戦う場面も多かったと指摘されている。',
      3: '騎馬は馬上で戦う武士を指し、中世を通じて武士の身分標識でもあった。ただし戦国期の日本在来馬は体高130センチ前後の小型馬であり、甲冑を着けた武者を乗せて長時間の突撃を反復する能力には限界があった。宣教師ルイス・フロイスは、日本の武士が馬で戦場に赴いたのち下馬して戦うことがあると記している。武田の騎馬隊に代表される「騎馬集団による突撃」の像は、江戸期の軍記・軍学書によって形成された部分が大きく、近年の研究では、騎馬は指揮・機動・追撃において重要だったが、密集突撃を主戦法としたわけではないと考えられている。ただし騎馬の比率や運用については史料的制約が大きく、なお議論が続いている。',
    },
    keywords: ['騎馬武者', '在来馬', '下馬戦闘', '軍記物', '武田'],
    caveat:
      '「騎馬隊が集団で突撃した」という描写は、江戸時代の軍記物に由来するところが大きい。在来馬の体格や下馬戦闘の記録から、近年はこの像に慎重な見方が有力である。',
  },
};

export const ARM_SOUHEI: ArmInfo = {
  arm: 'souhei',
  name: '僧兵',
  reading: 'そうへい',
  note: {
    summary: '寺の武装した勢力。延暦寺・興福寺・本願寺などが強大で、大名と正面から戦った。',
    detail: {
      1: '寺には、武器を持って戦う人たちがいました。比叡山の延暦寺や、大阪の石山本願寺は、大名と何年も戦えるほどの力を持っていました。豊臣秀吉の刀狩によって、寺や村から武器が取り上げられていきます。',
      2: '中世の大寺院は広大な荘園と多数の下級僧侶を抱え、武装勢力として独自の力を持っていた。延暦寺・興福寺の強訴は朝廷を動かし、浄土真宗の本願寺は各地の一向一揆と結んで戦国大名と戦った。織田信長の比叡山焼き討ち（1571）と、10年に及んだ石山合戦（1570〜80）は、その力を削ぐ過程だった。',
      3: '「僧兵」は寺院に属する武装集団を指す語だが、これは後世の呼称であり、同時代の史料では「悪僧」「大衆（だいしゅ）」などと記される。延暦寺・興福寺といった顕密寺院は荘園領主として経済基盤を持ち、強訴によって国家権力に要求を通した。戦国期に最大の軍事勢力となったのは浄土真宗本願寺で、門徒の一向一揆と結び、加賀では守護を倒して事実上の支配を100年近く維持した。織田信長との石山合戦は1570年から80年まで続き、毛利水軍の補給を受けて長期に渡り抗戦している。豊臣秀吉の刀狩令（1588）は、大仏建立の釘にするという名目で、百姓・寺社から武器を没収し、身分の固定と武装解除を同時に進めた。',
    },
    keywords: ['僧兵', '延暦寺', '本願寺', '一向一揆', '刀狩'],
    caveat:
      '「僧兵」は後世の呼称で、当時の史料には「悪僧」「大衆」などと記される。また武装したのは寺院の全構成員ではなく、下級僧侶や寺に属する在地の武力であった。',
  },
};

export const ARM_TEPPOU: ArmInfo = {
  arm: 'teppou',
  name: '鉄砲',
  reading: 'てっぽう',
  note: {
    summary: '1543年に種子島へ伝来した火縄銃。堺・国友・根来で量産され、合戦の形を変えた。',
    detail: {
      1: '1543年、種子島にポルトガル人が乗った船が流れつき、鉄砲が伝わりました。日本ではすぐに作り方が広まり、堺や国友などで大量に作られます。鉄砲は、長く訓練しなくても大きな力を出せる武器でした。',
      2: '1543年、種子島に漂着した船によって火縄銃が伝えられた。日本は当時すでに高い金属加工技術を持っており、堺・国友・根来といった産地で急速に量産が始まる。鉄砲は弓と違って射手の熟練に長い年月を要さないため、足軽に持たせて数をそろえることができ、合戦における遠戦の比重をさらに高めた。長篠の戦い（1575）はその代表例とされる。',
      3: '天文12年（1543）、種子島に漂着したポルトガル人によって火縄銃がもたらされたとされる。日本側には刀鍛冶を中心とする高度な金属加工の蓄積があり、ねじの製作という難所を越えたのち、堺・国友・根来・雑賀などで量産体制が築かれた。鉄砲の軍事的意義は貫通力そのものより、弓に比べて習熟に要する期間が短く、足軽に配備して数量を確保できた点にある。その結果、戦国後期の合戦では遠戦の比重がいっそう高まり、野戦築城（馬防柵・土塁）と組み合わせた防御戦術が発達した。ただし火薬の原料である硝石は国内産出が乏しく輸入に依存したため、鉄砲の運用は南蛮貿易・海上交通の掌握と不可分だった。',
    },
    year: 1543,
    keywords: ['火縄銃', '種子島', '堺', '国友', '長篠の戦い', '硝石'],
    caveat:
      '伝来年には1542年説もあり、また東アジアの交易網を通じて以前から断片的に知られていた可能性が指摘されている。長篠の戦いの「三段撃ち」は、同時代史料に裏付けがなく、現在では俗説とみるのが一般的である。',
  },
};

export const ARMS_INFO: readonly ArmInfo[] = [
  ARM_ASHIGARU,
  ARM_YUMI,
  ARM_KIBA,
  ARM_SOUHEI,
  ARM_TEPPOU,
];

export const armInfoOf = (arm: Arm): ArmInfo | undefined =>
  ARMS_INFO.find((info) => info.arm === arm);

// ---------------------------------------------------------------------------
// 章ごとの山札
// ---------------------------------------------------------------------------

const full = (arm: Arm) => ({ arm, maxRank: RANK_MAX });

/**
 * 刀狩（1588）後の僧兵。13枚 → 7枚。
 *
 * 全廃にはしない。刀狩は武装解除の完成ではなく身分の固定であり、
 * 寺社の勢力そのものが一夜で消えたわけではないため。
 */
const halvedSouhei = { arm: 'souhei' as const, maxRank: Math.ceil(RANK_MAX / 2) };

/**
 * 第I章（〜1543）— 52枚。
 *
 * 鉄砲はまだ存在しない。4兵種 × 13 で標準的なトランプとまったく同じ構成になり、
 * 役の出現数もほぼ一致する。**ポーカーを知っている人が説明なしで遊べる**という
 * 利点を、ここで確保している（`docs/06 §2`）。
 */
export const DECK_CH1: DeckSpec = {
  arms: [full('ashigaru'), full('yumi'), full('kiba'), full('souhei')],
};

/** 第II章（1543 鉄砲伝来〜）— 65枚。同じ数が5枚そろうので「無双」が成立する */
export const DECK_CH2: DeckSpec = {
  arms: [full('ashigaru'), full('yumi'), full('kiba'), full('souhei'), full('teppou')],
};

/** 第III章 — 第II章と同じ */
export const DECK_CH3: DeckSpec = DECK_CH2;

/** 第IV章（1588 刀狩〜）— 59枚。僧兵が半減する */
export const DECK_CH4: DeckSpec = {
  arms: [full('ashigaru'), full('yumi'), full('kiba'), halvedSouhei, full('teppou')],
};

/**
 * 章（1始まり）順の山札。`Ruleset.decks` としてそのまま core に渡す。
 *
 * 枚数が変わると役の出現率も動く。増減させるときは
 * `npm run sim:formations` を回して `docs/06 §16` の表と突き合わせること。
 */
export const DECKS: readonly DeckSpec[] = [DECK_CH1, DECK_CH2, DECK_CH3, DECK_CH4];
